import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useContext, useMemo, useRef, useState, useEffect } from 'react';
import { ConfigContext } from '../context/GlobalContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import BusinessDropdown from './child/BusinessDropdown';
import { useNavigate } from 'react-router-dom';
import * as actionType from "../context/actions";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const GST_STATE_CODES = {
    "01": "01-Jammu & Kashmir", "02": "02-Himachal Pradesh", "03": "03-Punjab",
    "04": "04-Chandigarh", "05": "05-Uttarakhand", "06": "06-Haryana",
    "07": "07-Delhi", "08": "08-Rajasthan", "09": "09-Uttar Pradesh",
    "10": "10-Bihar", "11": "11-Sikkim", "12": "12-Arunachal Pradesh",
    "13": "13-Nagaland", "14": "14-Manipur", "15": "15-Mizoram",
    "16": "16-Tripura", "17": "17-Meghalaya", "18": "18-Assam",
    "19": "19-West Bengal", "20": "20-Jharkhand", "21": "21-Odisha",
    "22": "22-Chhattisgarh", "23": "23-Madhya Pradesh", "24": "24-Gujarat",
    "25": "25-Daman & Diu", "26": "26-Dadra & Nagar Haveli", "27": "27-Maharashtra",
    "29": "29-Karnataka", "30": "30-Goa", "31": "31-Lakshadweep",
    "32": "32-Kerala", "33": "33-Tamil Nadu", "34": "34-Puducherry",
    "35": "35-Andaman & Nicobar Islands", "36": "36-Telangana",
    "37": "37-Andhra Pradesh", "38": "38-Ladakh"
};

/* ── Reusable custom dropdown (Standardized style) ── */
const CustomDropdown = ({ label, value, options, onSelect, searchable = false, placeholder = "Select" }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filtered = useMemo(() => {
        if (!search.trim()) return options;
        return options.filter(o => o.label?.toString().toLowerCase().includes(search.toLowerCase()));
    }, [options, search]);

    const selected = options.find(o => o.value === value);

    return (
        <div ref={ref} className="w-100">
            {label && <label className="form-label text-sm fw-semibold text-secondary-light mb-1">{label}</label>}
            <div className="dropdown">
                <button
                    className="btn btn-primary-600 bg-primary-50 border-primary-50 text-primary-600 dropdown-toggle w-100 d-flex justify-content-between align-items-center px-16 py-10 rounded"
                    type="button"
                    onClick={() => setOpen(prev => !prev)}
                >
                    <span style={{ fontSize: 14 }} className="text-truncate me-2">{selected?.label || placeholder}</span>
                </button>
                {open && (
                    <ul className="dropdown-menu w-100 show mt-1 shadow-sm border-0 rounded-3 text-center hide-scrollbar" style={{ padding: 0, minWidth: "100%", zIndex: 1050 }}>
                        {searchable && (
                            <li className="py-2 sticky-top z-2 bg-white">
                                <input
                                    type="text"
                                    className="form-control countrybackground mx-auto"
                                    style={{ width: "90%" }}
                                    placeholder={`Search...`}
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    autoFocus
                                />
                            </li>
                        )}
                        <li style={{ maxHeight: "250px", overflowY: "auto" }} className="hide-scrollbar">
                            <ul className="list-unstyled mb-0">
                                {filtered.length > 0 ? (
                                    filtered.map(o => (
                                        <li key={o.value}>
                                            <a
                                                href="#"
                                                className={`dropdown-item d-flex align-items-center gap-2 px-16 py-10 text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${value === o.value ? "bg-primary-50 fw-semibold" : ""}`}
                                                onClick={e => { e.preventDefault(); onSelect(o.value); setSearch(""); setOpen(false); }}
                                            >
                                                <span className="text-truncate">{o.label}</span>
                                                {value === o.value && <i className="bi bi-check2 text-primary-600 ms-auto"></i>}
                                            </a>
                                        </li>
                                    ))
                                ) : (
                                    <li className="text-center text-primary-600 small py-2">No results</li>
                                )}
                            </ul>
                        </li>
                    </ul>
                )}
            </div>
        </div>
    );
};

const Gstr1Layer = () => {
    const { state, dispatch } = useContext(ConfigContext);
    const navigate = useNavigate();

    const currentDate = new Date();
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [selectedCompanyId, setSelectedCompanyId] = useState(
        state?.BUSINESSES?.[0]?.companyId || ""
    );

    const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);
    const selectedBusiness = state?.BUSINESSES?.find(b => b.companyId === selectedCompanyId);

    const monthOptions = MONTH_NAMES.map((m, i) => ({ value: i, label: m }));
    const yearOptions = years.map(y => ({ value: y, label: y.toString() }));

    const sortedBusinesses = useMemo(() => {
        return [...(state?.BUSINESSES || [])]
            .sort((a, b) => new Date(a.creationDate) - new Date(b.creationDate));
    }, [state?.BUSINESSES]);

    // ===========================
    // PLAN CONFIG
    // ===========================
    const starterPlanName = process.env.REACT_APP_STARTER_PLAN_NAME;
    const professionalPlanName = process.env.REACT_APP_PROFESSIONAL_PLAN_NAME;
    const agencyPlanName = process.env.REACT_APP_AGENCY_PLAN_NAME;

    const userPlanName = state?.PAYMENT?.[state?.PAYMENT?.length - 1]?.planName || "Free Plan";
    const isAgencyPlan = userPlanName === agencyPlanName;
    const isProfessionalPlan = userPlanName === professionalPlanName;

    let allowedBusinessCount = 1; // Free & Starter
    if (isProfessionalPlan) allowedBusinessCount = 3;
    if (isAgencyPlan) allowedBusinessCount = sortedBusinesses.length;

    const handleBusinessChange = (companyId) => {
        setSelectedCompanyId(companyId);
        dispatch({ type: actionType.SELECTEDBUSINESS, payload: companyId });
    };

    const filteredInvoices = useMemo(() => {
        if (!state?.INVOICES) return [];
        return state.INVOICES.filter(inv => {
            if (inv.companyId !== selectedCompanyId) return false;

            // Only display invoices where currency is INR (or empty, which defaults to INR)
            const currency = inv.currency ? inv.currency.toString().toUpperCase() : "INR";
            if (currency !== "INR") return false;

            const ts = parseInt(inv.date?.length > 10 ? inv.date : inv.date * 1000);
            const d = new Date(ts);
            return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
        });
    }, [state?.INVOICES, selectedCompanyId, selectedMonth, selectedYear]);

    const rows = useMemo(() => {
        return filteredInvoices.map((inv, idx) => {
            const taxableValue = parseFloat(inv.subtotal || 0);
            const gstPct = parseFloat(inv.gstPercetage || 0);
            const totalTax = parseFloat(inv.gstAmount || ((taxableValue * gstPct) / 100));
            const isIgst = inv.isigst === "true" || inv.isigst === true;
            const cgst = isIgst ? 0 : totalTax / 2;
            const sgst = isIgst ? 0 : totalTax / 2;
            const igst = isIgst ? totalTax : 0;
            const ts = parseInt(inv.date?.length > 10 ? inv.date : inv.date * 1000);
            const dateStr = isNaN(ts) ? "-" : new Date(ts).toLocaleDateString("en-IN");
            const matchedCustomer = state?.CUSTOMERS?.find(
                (cust) => cust.customerId?.toString() === inv.customerId?.toString()
            );
            const customerName = matchedCustomer?.customer || inv.customer || "-";

            return {
                srNo: idx + 1,
                invoiceNo: inv.customInvoiceId || inv.invoiceId || "-",
                customerName: customerName,
                gstin: inv.customerGSTNo || matchedCustomer?.customerGSTNo || "-",
                date: dateStr,
                taxableValue: taxableValue.toFixed(2),
                gstPct,
                cgst: cgst.toFixed(2),
                sgst: sgst.toFixed(2),
                igst: igst.toFixed(2),
                totalTax: totalTax.toFixed(2),
                grandTotal: parseFloat(inv.totalAmount || 0).toFixed(2),
            };
        });
    }, [filteredInvoices]);

    const totals = useMemo(() => {
        return rows.reduce(
            (acc, r) => ({
                taxableValue: acc.taxableValue + parseFloat(r.taxableValue),
                cgst: acc.cgst + parseFloat(r.cgst),
                sgst: acc.sgst + parseFloat(r.sgst),
                igst: acc.igst + parseFloat(r.igst),
                totalTax: acc.totalTax + parseFloat(r.totalTax),
                grandTotal: acc.grandTotal + parseFloat(r.grandTotal),
            }),
            { taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalTax: 0, grandTotal: 0 }
        );
    }, [rows]);


    const sym = state?.SYMBOL === "$" ? `${state?.CURRENCYCODE} ${state?.SYMBOL}` : state?.SYMBOL;

    const handleExportPdf = () => {
        if (!state?.USERID) {
            dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
            return;
        }
        const doc = new jsPDF('l', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const businessName = selectedBusiness?.companyName || "All Businesses";
        const period = `${MONTH_NAMES[selectedMonth]} ${selectedYear}`;

        // Header Info
        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text("GSTR-1 Report (Outward Supplies)", pageWidth / 2, 15, { align: 'center' });

        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Business: ${businessName}`, pageWidth / 2, 22, { align: 'center' });
        doc.text(`Period: ${period}`, pageWidth / 2, 28, { align: 'center' });

        const tableBody = rows.map(r => [
            r.srNo,
            r.invoiceNo,
            r.customerName,
            r.gstin,
            r.date,
            r.taxableValue,
            `${r.gstPct}%`,
            r.cgst,
            r.sgst,
            r.igst,
            r.totalTax,
            r.grandTotal
        ]);

        autoTable(doc, {
            startY: 35,
            head: [['#', 'Inv No.', 'Customer', 'GSTIN', 'Date', 'Taxable', 'GST%', 'CGST', 'SGST', 'IGST', 'Tax', 'Total']],
            body: tableBody,
            foot: [['', '', '', 'Total', '', totals.taxableValue.toFixed(2), '', totals.cgst.toFixed(2), totals.sgst.toFixed(2), totals.igst.toFixed(2), totals.totalTax.toFixed(2), totals.grandTotal.toFixed(2)]],
            theme: 'grid',
            headStyles: { fillColor: [72, 127, 255], textColor: [255, 255, 255], fontStyle: 'bold' },
            footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
            styles: { fontSize: 8, cellPadding: 3 },
            columnStyles: {
                0: { cellWidth: 10 },
                1: { cellWidth: 20 }
            }
        });

        doc.save(`GSTR-1_${businessName.replace(/\s+/g, '_')}_${period.replace(/\s+/g, '_')}.pdf`);
    };

    const handleExportExcel = () => {
        if (!state?.USERID) {
            dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
            return;
        }
        const headers = ['#', 'Invoice No.', 'Customer', 'GSTIN', 'Date', 'Taxable', 'GST%', 'CGST', 'SGST', 'IGST', 'Tax', 'Total'];
        const csvRows = [
            [`GSTR-1 Report — ${MONTH_NAMES[selectedMonth]} ${selectedYear}`],
            [`Business: ${selectedBusiness?.companyName || "All"}`],
            [],
            headers,
            ...rows.map(r => [r.srNo, r.invoiceNo, `"${r.customerName}"`, r.gstin, r.date, r.taxableValue, `${r.gstPct}%`, r.cgst, r.sgst, r.igst, r.totalTax, r.grandTotal]),
            [],
            ['', '', '', '', 'Total', totals.taxableValue.toFixed(2), '', totals.cgst.toFixed(2), totals.sgst.toFixed(2), totals.igst.toFixed(2), totals.totalTax.toFixed(2), totals.grandTotal.toFixed(2)],
        ];
        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `GSTR-1_${MONTH_NAMES[selectedMonth]}_${selectedYear}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleExportGstr1B2B = () => {
        if (!state?.USERID) {
            dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
            return;
        }

        // Official B2B Headers
        const headers = [
            'GSTIN/UIN of Recipient',
            'Receiver Name',
            'Invoice Number',
            'Invoice date',
            'Invoice Value',
            'Place Of Supply',
            'Reverse Charge',
            'Applicable % of Tax Rate',
            'Invoice Type',
            'E-Commerce GSTIN',
            'Rate',
            'Taxable Value',
            'Cess Amount'
        ];

        // Filter and Map B2B Rows (Those with GSTIN)
        const b2bRows = rows
            .filter(r => r.gstin && r.gstin !== "-")
            .map(r => {
                const stateCode = r.gstin.substring(0, 2);
                const pos = GST_STATE_CODES[stateCode] || "Other";
                // Format date from DD/MM/YYYY to DD-MM-YYYY
                const dParts = r.date.split("/");
                const fmtDate = dParts.length === 3 ? `${dParts[0].padStart(2, '0')}-${dParts[1].padStart(2, '0')}-${dParts[2]}` : r.date;

                return [
                    r.gstin,
                    `"${r.customerName}"`,
                    r.invoiceNo,
                    fmtDate,
                    r.grandTotal,
                    pos,
                    'N',
                    '',
                    'Regular',
                    '',
                    r.gstPct,
                    r.taxableValue,
                    '0'
                ];
            });

        if (b2bRows.length === 0) {
            alert("No B2B invoices (with GSTIN) found in the selected period.");
            return;
        }

        const csvContent = [headers, ...b2bRows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const businessName = selectedBusiness?.companyName || "Business";
        a.download = `GSTR1_B2B_${businessName.replace(/\s+/g, '_')}_${MONTH_NAMES[selectedMonth]}_${selectedYear}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const summaryCards = [
        { label: "Total Taxable Value", value: totals.taxableValue.toFixed(2), icon: "mdi:currency-inr", color: "#487fff" },
        { label: "Total CGST", value: totals.cgst.toFixed(2), icon: "mdi:receipt", color: "#3dc4c4" },
        { label: "Total SGST", value: totals.sgst.toFixed(2), icon: "mdi:receipt", color: "#45c15d" },
        { label: "Total IGST", value: totals.igst.toFixed(2), icon: "mdi:receipt", color: "#f4a60a" },
        { label: "Total Tax", value: totals.totalTax.toFixed(2), icon: "mdi:cash-multiple", color: "#ee4444" },
        { label: "Grand Total", value: totals.grandTotal.toFixed(2), icon: "mdi:check-circle-outline", color: "#487fff" },
    ];

    return (
        <div className="card h-100">
            {/* ── Card Header: Filters + Buttons ── */}
            <div className="card-header no-print">
                <div className="row gy-3 align-items-end">
                    {/* Left — Filters */}
                    <div className="col-12 col-md-9 px-0">
                        <div className="d-flex flex-wrap align-items-end gap-3 px-0">
                            <div style={{ minWidth: "220px" }}>
                                <label className="form-label text-sm fw-semibold text-secondary-light mb-1">Business</label>
                                <BusinessDropdown
                                    businesses={sortedBusinesses}
                                    selectedCompanyId={selectedCompanyId}
                                    onBusinessChange={handleBusinessChange}
                                    onPricingNavigate={() => navigate("/pricing")}
                                    showAllOption={true}
                                    isAllowedCheck={(business) => {
                                        const ownBusinesses = sortedBusinesses.filter(b => !b.isShared);
                                        const allowedOwnIds = ownBusinesses
                                            .slice(-allowedBusinessCount)
                                            .map(b => b.companyId);

                                        return (
                                            business.isShared ||
                                            isAgencyPlan ||
                                            allowedOwnIds.includes(business.companyId)
                                        );
                                    }}
                                />
                            </div>
                            <div style={{ width: "160px" }}>
                                <CustomDropdown
                                    label="Month"
                                    value={selectedMonth}
                                    options={monthOptions}
                                    onSelect={setSelectedMonth}
                                />
                            </div>
                            <div style={{ width: "120px" }}>
                                <CustomDropdown
                                    label="Year"
                                    value={selectedYear}
                                    options={yearOptions}
                                    onSelect={setSelectedYear}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right — Export Options */}
                    <div className="col-12 col-md-3">
                        <div className="d-flex justify-content-md-end gap-2 flex-wrap">
                            <button
                                className="btn btn-sm btn-outline-primary-600 radius-8 d-flex align-items-center gap-2"
                                onClick={handleExportPdf}
                                disabled={rows.length === 0}
                            >
                                <Icon icon="mdi:file-pdf-outline" className="text-xl" />
                                Export PDF
                            </button>
                            {/* <button
                                className="btn btn-sm btn-outline-success radius-8 d-flex align-items-center gap-2"
                                onClick={handleExportGstr1B2B}
                                disabled={rows.length === 0}
                            >
                                <Icon icon="mdi:file-export-outline" className="text-xl" />
                                Export for CA (B2B)
                            </button> */}
                            <button
                                className="btn btn-sm btn-primary-600 radius-8 d-flex align-items-center gap-2"
                                onClick={handleExportExcel}
                                disabled={rows.length === 0}
                            >
                                <Icon icon="mdi:microsoft-excel" className="text-xl" />
                                Export Excel
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card-body">

                {/* Info Note for INR/GSTR-1 limitation */}
                <div className="alert alert-info d-flex align-items-center gap-2 mb-4 radius-8" role="alert" style={{ fontSize: "14px", border: "1px solid #487fff33", backgroundColor: "#487fff12", color: "#487fff" }}>
                    <Icon icon="lucide:info" className="fs-5" />
                    <div>
                        <strong>Note:</strong> GSTR-1 is an Indian tax report. Only invoices with INR currency are included in this report.
                    </div>
                </div>

                {/* ── Summary Cards ── */}
                <div className="row g-3 mb-4">
                    {summaryCards.map((card, i) => (
                        <div key={i} className="col-6 col-md-4 col-xl-2">
                            <div
                                className="radius-8 p-3 h-100 d-flex flex-column gap-2"
                                style={{
                                    border: `1px solid ${card.color}33`,
                                    background: `${card.color}12`,
                                }}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <span
                                        className="w-32-px h-32-px radius-6 d-flex align-items-center justify-content-center"
                                        style={{ background: `${card.color}22` }}
                                    >
                                        <Icon icon={card.icon} style={{ color: card.color, fontSize: 18 }} />
                                    </span>
                                    <span className="text-secondary-light" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                        {card.label}
                                    </span>
                                </div>
                                <p className="mb-0 fw-bold" style={{ color: card.color, fontSize: 15 }}>
                                    {sym} {card.value}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Invoice Table ── */}
                {rows.length === 0 ? (
                    <div className="text-center py-5">
                        <Icon icon="mdi:file-document-outline" className="text-4xl text-secondary-light opacity-50 mb-3" />
                        <p className="mb-0 text-secondary-light">No invoices found for the selected period.</p>
                    </div>
                ) : (
                    <>
                        <style>{`
                            .hide-scrollbar-custom::-webkit-scrollbar { display: none; }
                            .hide-scrollbar-custom { -ms-overflow-style: none; scrollbar-width: none; }
                        `}</style>
                        <div className="table-responsive hide-scrollbar-custom">
                            <table className="table bordered-table text-sm">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Invoice No.</th>
                                        <th>Customer</th>
                                        <th>GSTIN</th>
                                        <th>Date</th>
                                        <th>Taxable</th>
                                        <th>GST%</th>
                                        <th>CGST</th>
                                        <th>SGST</th>
                                        <th>IGST</th>
                                        <th>Tax</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => (
                                        <tr key={r.srNo}>
                                            <td>{r.srNo}</td>
                                            <td className="fw-medium text-primary-600">{r.invoiceNo}</td>
                                            <td>{r.customerName}</td>
                                            <td className="text-secondary-light" style={{ fontSize: 12 }}>{r.gstin}</td>
                                            <td>{r.date}</td>
                                            <td>{r.taxableValue}</td>
                                            <td>
                                                <span className="badge text-sm" style={{ background: "#487fff22", color: "#487fff" }}>
                                                    {r.gstPct}%
                                                </span>
                                            </td>
                                            <td>{r.cgst}</td>
                                            <td>{r.sgst}</td>
                                            <td>{r.igst}</td>
                                            <td className="fw-semibold">{r.totalTax}</td>
                                            <td className="fw-bold text-primary-600">{r.grandTotal}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="fw-bold" style={{ borderTop: "2px solid var(--primary-200)" }}>
                                        <td colSpan={5} className="text-end text-primary-600" style={{ backgroundColor: "transparent" }}>Totals</td>
                                        <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{totals.taxableValue.toFixed(2)}</td>
                                        <td className="text-secondary-light" style={{ backgroundColor: "transparent" }}>—</td>
                                        <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{totals.cgst.toFixed(2)}</td>
                                        <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{totals.sgst.toFixed(2)}</td>
                                        <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{totals.igst.toFixed(2)}</td>
                                        <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{totals.totalTax.toFixed(2)}</td>
                                        <td className="text-primary-600" style={{ backgroundColor: "transparent" }}>{totals.grandTotal.toFixed(2)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Gstr1Layer;
